import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarCheck, Clock, User, CheckCircle2, XCircle, Eye, RefreshCw, Filter, Search } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200',
  completed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200',
  cancelled: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  expired: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200',
  verification_pending: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200',
  rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200'
};

const statusLabels = {
  pending: 'Pendiente',
  completed: 'Completada',
  cancelled: 'Cancelada',
  expired: 'Expirada',
  verification_pending: 'Verificando',
  rejected: 'Rechazada'
};

export const BusinessReservationsSection = ({ businessId }) => {
  const { t } = useTranslation();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReservation, setSelectedReservation] = useState(null);

  useEffect(() => {
    fetchReservations();
  }, [businessId]);

  const fetchReservations = async () => {
    try {
      const response = await axios.get(`${API}/reservations/business`, {
        headers: { 'X-Business-Id': businessId }
      });
      setReservations(response.data);
    } catch (error) {
      console.error('Error fetching reservations:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsCompleted = async (reservationId) => {
    try {
      await axios.patch(`${API}/reservations/${reservationId}/complete`, {}, {
        headers: { 'X-Business-Id': businessId }
      });
      toast.success('Reserva marcada como completada');
      fetchReservations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error actualizando reserva');
    }
  };

  const filteredReservations = reservations.filter(r => {
    const matchesFilter = filter === 'all' || r.status === filter;
    const matchesSearch = searchTerm === '' || 
      r.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.kupon_title?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const pendingCount = reservations.filter(r => r.status === 'pending').length;
  const todayCount = reservations.filter(r => {
    const today = new Date().toDateString();
    const reservationDate = new Date(r.reservation_date).toDateString();
    return reservationDate === today && r.status === 'pending';
  }).length;

  if (loading) {
    return (
      <div className="bg-card rounded-2xl p-6 shadow-card border border-border/50">
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card rounded-2xl p-6 shadow-card border border-border/50"
      data-testid="business-reservations-section"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center text-white">
            <CalendarCheck className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-lg">Reservas Entrantes</h3>
            <p className="text-sm text-muted-foreground">
              {pendingCount} pendientes • {todayCount} para hoy
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchReservations}
          data-testid="refresh-reservations-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por código o kupon..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-reservations"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {[
            { value: 'all', label: 'Todas' },
            { value: 'pending', label: 'Pendientes' },
            { value: 'verification_pending', label: 'Verificando' },
            { value: 'completed', label: 'Completadas' }
          ].map(f => (
            <Button
              key={f.value}
              variant={filter === f.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f.value)}
              className={filter === f.value ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
              data-testid={`filter-${f.value}`}
            >
              {f.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Reservations List */}
      {filteredReservations.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
          <CalendarCheck className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">
            {filter === 'all' ? 'No hay reservas aún' : `No hay reservas ${statusLabels[filter]?.toLowerCase() || ''}`}
          </p>
        </div>
      ) : (
        <div className="space-y-3 max-h-[600px] overflow-y-auto">
          {filteredReservations.map((reservation) => (
            <motion.div
              key={reservation.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="border border-border/50 rounded-xl p-4 hover:border-emerald-200 dark:hover:border-emerald-800 transition-colors"
              data-testid={`reservation-${reservation.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-mono text-lg font-bold text-emerald-600">
                      {reservation.id.slice(0, 8).toUpperCase()}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full ${statusColors[reservation.status]}`}>
                      {statusLabels[reservation.status]}
                    </span>
                  </div>
                  
                  <p className="font-medium text-sm mb-1">{reservation.kupon_title}</p>
                  
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(reservation.reservation_date).toLocaleString('es-ES', {
                        day: '2-digit',
                        month: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      Cliente #{reservation.user_id?.slice(0, 6)}
                    </span>
                  </div>
                  
                  {reservation.notes && (
                    <p className="text-xs text-muted-foreground mt-2 italic">
                      "{reservation.notes}"
                    </p>
                  )}
                </div>

                <div className="flex gap-2">
                  {reservation.status === 'pending' && (
                    <Button
                      size="sm"
                      onClick={() => markAsCompleted(reservation.id)}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid={`complete-${reservation.id}`}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      Completar
                    </Button>
                  )}
                  
                  {reservation.status === 'verification_pending' && reservation.ticket_image && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSelectedReservation(reservation)}
                      data-testid={`view-ticket-${reservation.id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Ver ticket
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Ticket Preview Modal */}
      <AnimatePresence>
        {selectedReservation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedReservation(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-card rounded-2xl p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="font-semibold text-lg mb-4">
                Ticket de Reserva #{selectedReservation.id.slice(0, 8).toUpperCase()}
              </h3>
              
              {selectedReservation.ticket_image && (
                <img
                  src={selectedReservation.ticket_image}
                  alt="Ticket"
                  className="w-full rounded-lg mb-4"
                />
              )}
              
              {selectedReservation.verification_result && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-sm">
                  <p className="font-medium mb-2">Resultado de verificación:</p>
                  <pre className="whitespace-pre-wrap text-xs text-muted-foreground">
                    {JSON.stringify(selectedReservation.verification_result, null, 2)}
                  </pre>
                </div>
              )}
              
              <Button
                variant="outline"
                className="w-full mt-4"
                onClick={() => setSelectedReservation(null)}
              >
                Cerrar
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default BusinessReservationsSection;
